/* eslint-disable react-hooks/rules-of-hooks */
import { useCallback, useEffect, useMemo, useRef, useState, type ComponentProps, type ReactNode } from 'react';
import { produce } from 'immer';
import { isEqual, uniq } from 'lodash-es';
import { useQueryClient } from 'react-query';
import { createContext, useContextSelector } from 'use-context-selector';
import { TeamChatTypes } from '@frontend/api-team-chat';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useScopedAppFlagStore } from '@frontend/scope';
import { usePreference } from '@frontend/user-preferences';
import { useUserPresence } from '@frontend/userpresence';
import { useLocalStorage } from '@frontend/web-storage';
import { cascadeThreadMessageUp } from '../../utils';
import { _TeamChatActiveConversationProvider } from '../active-conversation.provider/active-conversation.provider';
import { _SubscriptionsManager } from './_subscriptions/_subscriptions.manager';
import { convertUserPresenceDataToOrgIdFormat } from './_subscriptions/_use-presence-sync';
import { useGetUsers } from './hooks/use-get-users.hook';
import { useUserMessageDetails } from './hooks/use-user-message-details';
import { _StreamAPIProvider, _StreamAPIContext } from './team-chat-api-providers/stream-api-provider';
import { useTeamChatApi } from './team-chat-api-providers/use-team-chat-api';

/**
 *
 * Layout of this provider tree
 *      - TeamChatProvider (handles connection, data fetching and exposes the context)
 *          - SubscriptionsManager (handles subscriptions)
 *          - TeamChatActiveConversationProvider (handles active conversation)
 *            - children
 */

export type TeamChatContextValue = ReturnType<typeof useTeamChatProvider>;
const TeamChatContext = createContext<ReturnType<typeof useTeamChatProvider> | undefined>(undefined);

type ProviderProps = {
  orgId: string;
  userId: string;
  api: 'stream' | 'mock';
  children: ReactNode;
  _setKeepTeamChatClientOpen?: (value: boolean) => void;
  isThreadsSectionEnabled: boolean;
  shouldFetchConversationUsers: boolean;
};

type UserProps = {
  firstName: string;
  lastName: string;
  userID: string;
  status: {
    presence: {
      online: boolean;
      expiresAt: string;
      updatedAt: string;
    };
    status: any;
  };
  isDeactivated: boolean;
};

const _TeamChatProviderInner = ({
  orgId,
  userId,
  api,
  children,
  _setKeepTeamChatClientOpen,
  isThreadsSectionEnabled,
  ...clientProps
}: ProviderProps) => {
  const value = useTeamChatProvider({
    orgId,
    userId,
    api,
    _setKeepTeamChatClientOpen,
    isThreadsSectionEnabled,
    ...clientProps,
  });
  const { isReady, user } = value;
  return (
    <TeamChatContext.Provider value={value}>
      <_TeamChatActiveConversationProvider
        activeConversationId={value.activeConversationId}
        selectedThreadParentId={value.selectedThreadParentId}
        enabled={isReady}
      >
        {user && (
          <_SubscriptionsManager
            orgId={orgId}
            activeConversationId={value.activeConversationId}
            clientUserId={user.userID}
          />
        )}
        {children}
      </_TeamChatActiveConversationProvider>
    </TeamChatContext.Provider>
  );
};
_TeamChatProviderInner.displayName = 'TeamChatProvider';

export const useTeamChatProvider = ({
  orgId,
  userId: _userId,
  api,
  _setKeepTeamChatClientOpen,
  isThreadsSectionEnabled,
  shouldFetchConversationUsers,
}: {
  orgId: string;
  userId: string;
  api: 'stream' | 'mock';
  _setKeepTeamChatClientOpen?: (value: boolean) => void;
  isThreadsSectionEnabled: boolean;
  shouldFetchConversationUsers: boolean;
}) => {
  const { userPresenceData: userpresence } = useUserPresence();
  const queryClient = useQueryClient();
  const [isAsleep, setIsAsleep] = useState(false);
  const [mainPanelView, _setMainPanelView] = useState<
    'mentions' | 'conversation-static' | 'conversation-dynamic' | 'unread' | 'thread' | 'threads-section' | 'none'
  >('none');
  const [isOpen, setIsOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [isNavExpanded, setIsNavExpanded] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [activeConversationId, _setActiveConversationId] = useState<string>();
  const [selectedThreadParentId, _setSelectedThreadParentId] = useState<string>();
  const [highlightedMessageId, _setHighlightedMessageId] = useState<string>();
  const [isCurrentUserStatusOpen, _setIsCurrentUserStatusOpen] = useState(false);
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isHideConversationEnabled = getFeatureFlagValue('team-chat-hide-conversation');
  const isKeywordSearchEnabled = getFeatureFlagValue('team-chat-keyword-search');
  const [unreadMentionsCount, setUnreadMentionsCount] = useState(0);
  const isSmallModeEnabled = useMatchMedia({ maxWidth: breakpoints.medium.min });
  const isNavigatingFromThreads = useRef<boolean>(false);
  const parentMessageRef = useRef<TeamChatTypes.Message | undefined>(undefined);

  const {
    user,
    setUser,
    isInitialized,
    isInitializing,
    reInitialize,
    initializationError,
    disconnect,
    reconnect,
    destroy,
    setReplies,
    removeRepliesQuery,
    useQueryConversations,
    useQueryMentions,
    useQueryThreads,
    useGetUnreadThreadsCount,
    useMutationUpdateUser,
    useMutationCreateConversation,
    useMutationUpdateConversation,
    useMutationDeleteConversation,
    useMutationAddMembersToConversation,
    useMutationRemoveMembersFromConversation,
  } = useTeamChatApi(api);
  const clientUserId = user?.userID;

  const _sleep = () =>
    disconnect().then(() => {
      setIsAsleep(true);
    });

  const _wake = () => {
    return reconnect()
      .catch((err) => {
        console.error('Failed to reconnect client', err);
      })
      .finally(() => {
        setIsAsleep(false);
      });
  };

  const [hideConversationModal, setHideConversationModal] = usePreference('TeamChatHideConversationModalHide');
  const [hideLinksSecurityModal, setHideLinksSecurityModal] = usePreference('TeamChatHideLinksSecurityModal');

  const addUserPresence = useCallback(
    (data: UserProps[]) => {
      const userpresenceWithOrgId = convertUserPresenceDataToOrgIdFormat(orgId, userpresence);
      return data.map((userData: UserProps) => {
        const presence = userpresenceWithOrgId[userData.userID];
        return {
          ...userData,
          status: {
            ...userData.status,
            presence: {
              ...userData.status.presence,
              online: presence?.userStatus === 'ONLINE' || false,
            },
          },
        };
      });
    },
    [orgId, userpresence]
  );

  const {
    data: conversations,
    isLoading: isLoadingConversations,
    isFetched: isConversationsFetched,
    remove: _removeConversations,
    error: conversationsError,
    refetch: refetchConversations,
    set: setConversations,
    queryKey: conversationQueryKey,
  } = useQueryConversations({ shouldFetchHiddenConversations: isHideConversationEnabled });

  const { otherUsers, isLoadingUsers, isUsersFetched, _removeUsers, usersError, refetchUsers, setUsers } = useGetUsers({
    conversationQueryKey,
    shouldFetchConversationUsers,
    api,
  });

  const rawUsers = useMemo(() => [...(otherUsers ?? []), ...(user ? [user] : [])], [user, otherUsers]);
  const users = useMemo(() => addUserPresence(rawUsers), [addUserPresence, rawUsers]);

  const {
    data: messagesWithMentions,
    isLoading: isLoadingMentions,
    isFetched: isMentionsFetched,
    isFetching: isFetchingMentions,
    remove: _removeMentions,
    error: mentionsError,
    refetch: refetchMentions,
    set: setMentions,
  } = useQueryMentions(100, 0, {});

  const { set: setQueryThreads, ...threadsQueryProps } = useQueryThreads(orgId, clientUserId ?? '', {
    enabled: isThreadsSectionEnabled,
  });

  // Creating a map of thread messages with their corresponding parent message id with it's page and row index for
  // better search in the function below
  const threadsParentIdMap: Map<string, TeamChatTypes.Thread & { pageIndex: number; rowIndex: number }> =
    useMemo(() => {
      const map = new Map();
      if (threadsQueryProps.data?.pages.length) {
        threadsQueryProps.data.pages.forEach(({ rows }, pageIndex) => {
          if (rows.length) {
            rows.forEach((thread, rowIndex) => {
              map.set(thread.parentMessage.id, { ...thread, pageIndex, rowIndex });
            });
          }
        });
      }
      return map;
    }, [threadsQueryProps.data]);

  const {
    data: threadsUnreadCount,
    refetch: refetchUnreadCount,
    set: setThreadsUnreadCount,
  } = useGetUnreadThreadsCount(orgId, { enabled: isThreadsSectionEnabled });

  const {
    mutateAsync: updateCurrentUser,
    isLoading: isUpdatingUser,
    error: updateUserError,
  } = useMutationUpdateUser({
    onSuccess: (user) => {
      cache.updateCurrentUser(user);
    },
  });

  const {
    mutateAsync: createConversation,
    isLoading: isCreatingConversation,
    error: createConversationError,
  } = useMutationCreateConversation({
    onSuccess: (conversation) => {
      cache.addConversation(conversation);
      openStaticConversation(conversation.channelId);
    },
  });

  const {
    mutateAsync: updateConversation,
    isLoading: isUpdatingConversation,
    error: updateConversationError,
  } = useMutationUpdateConversation({
    onSuccess: (conversation) => {
      cache.updateConversation(conversation.channelId, conversation);
    },
  });

  const {
    mutateAsync: deleteConversation,
    isLoading: isDeletingConversation,
    error: deleteConversationError,
  } = useMutationDeleteConversation({
    onSuccess: (conversation) => {
      cache.removeConversation(conversation.channelId);
      if (conversation.channelId === activeConversationId) {
        _setActiveConversationId(undefined);
      }
    },
  });

  const { mutateAsync: addMembersToConversation, isLoading: isAddingMembers } = useMutationAddMembersToConversation({
    onSuccess: (userIds, vars) => {
      cache.updateConversation(vars.conversationId, { memberIds: userIds });
    },
  });

  const { mutateAsync: removeMembersFromConversation, isLoading: isRemovingMembers } =
    useMutationRemoveMembersFromConversation({
      onSuccess: (userIds, vars) => {
        cache.updateConversation(vars.conversationId, { memberIds: userIds });
        // userIds returned from the api call contains the users that remain in the conversation
        // if that list doesn't include the current user, we should remove the conversation from the cache
        if (user?.userID && !userIds.includes(user?.userID)) {
          cache.removeConversation(vars.conversationId);
          if (vars.conversationId === activeConversationId) {
            closeConversation();
          }
        }
      },
    });

  const [notificationSettings, setNotificationSettings] = useLocalStorage<{
    showWhenOpen: boolean;
    showOnActiveConversation: boolean;
  }>({
    key: 'team-chat-notification-settings-v2',
    defaultValue: {
      showWhenOpen: true,
      showOnActiveConversation: false,
    },
  });

  /**
   * The cache is for updating the local version of the data without making any api calls
   */

  /** Just appending self user on to the otherUsers so it includes the current user */
  const cache = useMemo(() => {
    const conversationUpdater = (conversationId: string, updater: (conversation: Conversation) => Conversation) => {
      setConversations((prev) => {
        if (!prev) {
          return prev;
        }
        const conversation =
          prev?.dm.find((c) => c.channelId === conversationId) ??
          prev?.groups.find((c) => c.channelId === conversationId);

        if (!conversation) {
          return prev;
        }
        const type = conversation.type === 'DM' ? 'dm' : ('groups' as const);
        const updatedConversation = {
          ...conversation,
          ...updater(conversation),
        } satisfies typeof conversation;
        if (isEqual(conversation, updatedConversation)) {
          return prev;
        }

        const next = prev
          ? {
              dm:
                type === 'dm'
                  ? prev.dm.map((c) => (c.channelId === conversation.channelId ? { ...c, ...updatedConversation } : c))
                  : prev.dm,
              groups:
                type === 'groups'
                  ? prev.groups.map((c) =>
                      c.channelId === conversation.channelId ? { ...c, ...updatedConversation } : c
                    )
                  : prev.groups,
            }
          : prev;

        return next;
      });
    };

    const updateQueryThreadsCache: TeamChatTypes.GenericHandler = (event, payload) => {
      const message = payload.message;
      const isSelfUser = payload.user?.userID === payload.currentUser.userID;
      const threadReadData = payload.threadReadData;

      const isThreadInCache = (messageParentId: string) => threadsParentIdMap.has(messageParentId);
      const isUnreadCountFetched = typeof threadsUnreadCount === 'number';

      // Helpers for better readability
      const isMarkReadEventForSelfUser = threadReadData && event === 'notification.mark_read' && isSelfUser;

      const isMarkReadForCachedThread =
        isMarkReadEventForSelfUser && threadReadData && isThreadInCache(threadReadData.parentMessageId);

      const parentMessageId = message?.parentId ? message.parentId : message?.id;

      const isMessageWithUncachedThread = parentMessageId && !isThreadInCache(parentMessageId);

      const isLastReplyBeingDeleted = () => {
        if (event !== 'message.deleted' || !message?.parentId || !isThreadInCache(message?.parentId)) {
          return false;
        }
        const threadInfo = threadsParentIdMap.get(message?.parentId);
        return threadInfo?.latestReplies.length === 1;
      };

      // Determine if we need to refetch
      const shouldRefetch =
        isMarkReadForCachedThread ||
        isMessageWithUncachedThread ||
        isLastReplyBeingDeleted() ||
        event === 'member.removed';

      // Handle refetch scenarios
      if (shouldRefetch) {
        threadsQueryProps.refetch();
        if (isMessageWithUncachedThread) {
          refetchUnreadCount();
        } else if (isMarkReadEventForSelfUser && isUnreadCountFetched && threadsUnreadCount > 0) {
          setThreadsUnreadCount(threadsUnreadCount - 1);
        }
        return;
      }

      // Early returns for cases that don't need cache updates
      if (!message || isMessageWithUncachedThread || !parentMessageId) {
        return;
      }

      setQueryThreads((prev) => {
        const threadInfo = threadsParentIdMap.get(parentMessageId)!;
        if (!prev || !threadInfo) return prev;
        const { pageIndex, rowIndex, ...thread } = threadInfo;

        const newPages = prev.pages.map((page) => ({
          ...page,
          rows: page.rows.map((row) => ({ ...row })),
        }));
        const threadPageRef = newPages?.[pageIndex]?.rows?.[rowIndex];

        // This will update the parent message, as it checks if there is parent id on the message object which specifies
        // that this is specifically for a parent message
        if (!message.parentId && threadPageRef) {
          newPages[pageIndex].rows[rowIndex] = {
            ...threadPageRef,
            parentMessage: { ...message },
          };
        }
        // Handle message.new for replies - need to move thread to top of first page
        else if (event === 'notification.thread_message_new' && message.parentId) {
          if (isUnreadCountFetched && !isSelfUser && !threadInfo?.unreadCount) {
            setThreadsUnreadCount(threadsUnreadCount + 1);
          }

          /*
            Directly using the page to update the thread and if page is not present then we will fallback to the
            threads reference map. When we update the page the map does not updates itself even when using useMemo
            That is why I'm picking the page to update the data so that we do not rely on old data, and threadInfo will
            only give us the page and row index
           */
          const pageToUse = threadPageRef ?? thread;

          const updatedThread: TeamChatTypes.Thread = {
            ...pageToUse,
            latestReplies: [...pageToUse.latestReplies, message],
            replyCount: pageToUse.replyCount + 1,
            ...(!isSelfUser && { unreadCount: pageToUse.unreadCount + 1 }),
          };

          const cascadedPages = cascadeThreadMessageUp(newPages, threadInfo, updatedThread);

          return { ...prev, pages: cascadedPages };
        } else if (event === 'message.deleted') {
          if (thread.latestReplies.length === 1) {
            if (isUnreadCountFetched && !!thread.unreadCount) {
              setThreadsUnreadCount(threadsUnreadCount - 1);
            }
            return prev;
          }
          if (threadPageRef) {
            newPages[pageIndex].rows[rowIndex] = {
              ...threadPageRef,
              latestReplies: threadPageRef.latestReplies.filter((msg) => msg.id !== message.id),
              replyCount: threadPageRef.replyCount - 1,
              unreadCount: threadPageRef.unreadCount > 0 ? threadPageRef.unreadCount - 1 : threadPageRef.unreadCount,
            };
          }
        } else {
          if (threadPageRef) {
            newPages[pageIndex].rows[rowIndex] = {
              ...threadPageRef,
              latestReplies: threadPageRef.latestReplies.map((msg) => (msg.id === message.id ? message : msg)),
            };
          }
        }
        return { ...prev, pages: newPages };
      });
    };

    return {
      setUsers,
      setConversations,
      setUnreadMentionsCount,
      updateQueryThreadsCache,
      updateCurrentUser: (userUpdates: Pick<User, 'firstName' | 'lastName' | 'status'>) => {
        setUser((prev) => (prev ? { ...prev, ...userUpdates } : prev));
      },
      addUser: (user: User) => {
        setUsers((prev) => (prev ? [...prev, user] : [user]));
      },
      removeUser: (userId: string) => {
        setUsers((prev) => (prev ? prev.filter((u) => u.userID !== userId) : prev));
      },
      updateUser: (userId: string, user: Partial<User>) => {
        setUsers((prev) =>
          produce(prev, (draft) => {
            if (!draft) return draft;

            const userIndex = draft.findIndex((u) => u.userID === userId);

            if (userIndex !== -1) {
              const u = draft[userIndex];
              const online = u.status?.presence?.online;

              draft[userIndex] = {
                ...u,
                ...user,
                status: {
                  ...u.status,
                  ...user.status,
                  presence: {
                    ...u.status?.presence,
                    ...user.status?.presence,
                    online, // persist the original online status
                  },
                },
              };
            }
          })
        );
      },
      addConversation: (conversation: Conversation) => {
        const type = conversation.type === 'DM' ? 'dm' : ('groups' as const);
        setConversations((prev) => {
          const isDuplicate = prev?.[type].some((c) => c.channelId === conversation.channelId);
          if (isDuplicate) {
            return prev;
          }
          return prev ? { ...prev, [type]: [...prev[type], conversation] } : prev;
        });
      },

      removeConversation: (conversationId: string) => {
        setConversations((prev) =>
          prev
            ? {
                dm: prev.dm.filter((c) => c.channelId !== conversationId),
                groups: prev.groups.filter((c) => c.channelId !== conversationId),
              }
            : prev
        );
      },
      incrementConversationUnreadCount: (conversationId: string) => {
        conversationUpdater(conversationId, (conversation) => ({
          ...conversation,
          unreadCount: conversation.unreadCount ? conversation.unreadCount + 1 : 1,
        }));
      },
      addUserTypingToConversation: (conversationId: string, userId: string) => {
        conversationUpdater(conversationId, (conversation) => {
          const next = conversation.usersTyping.includes(userId)
            ? conversation
            : {
                ...conversation,
                usersTyping: [...conversation.usersTyping, userId],
              };
          return next;
        });
      },
      removeUserTypingFromConversation: (conversationId: string, userId: string) => {
        conversationUpdater(conversationId, (conversation) => {
          const next = conversation.usersTyping.includes(userId)
            ? {
                ...conversation,
                usersTyping: conversation.usersTyping.filter((u) => u !== userId),
              }
            : conversation;
          return next;
        });
      },
      updateConversation: (
        conversationId: string,
        updates: Partial<Conversation> | Parameters<typeof conversationUpdater>[1]
      ) => {
        return conversationUpdater(conversationId, (conversation) =>
          typeof updates === 'function' ? updates(conversation) : { ...conversation, ...updates }
        );
      },

      removeMentionedMessage: (messageId: string) => {
        setMentions((prev) => (prev ? prev.filter((m) => m.id !== messageId) : []));
      },
      addUnreadMentionedMessage: (message: Message) => {
        setMentions((prev) => (prev ? [{ ...message, isUnread: true }, ...prev] : [message]));
        setUnreadMentionsCount((prev) => (prev ? prev + 1 : 1));
      },
      markMentionedMessageAsRead: (messageId: string) => {
        setMentions((prev) => (prev ? prev.map((m) => (m.id === messageId ? { ...m, isUnread: false } : m)) : prev));
        setUnreadMentionsCount((prev) => Math.max(0, prev ? prev - 1 : 0));
      },
      markConversationMentionedMessagesAsRead: (conversationId: string) => {
        let marked = 0;
        setMentions((prev) => {
          if (!prev) return prev;
          return prev.map((m) => {
            if (m.channelId === conversationId && m.isUnread) {
              marked += 1;
              return { ...m, isUnread: false };
            }
            return m;
          });
        });
        if (marked > 0 && !!unreadMentionsCount) {
          //TODO: for some reason the 'prev' here isn't getting up to date. I'm not sure why, so I'm bypassing it with the local state value for now
          setUnreadMentionsCount((_prev) => Math.max(0, unreadMentionsCount ? unreadMentionsCount - marked : 0));
        }
      },
      removeUnreadMentionedMessage: (messageId: string) => {
        setMentions((prev) => (prev ? prev.filter((m) => m.id !== messageId) : []));
        setUnreadMentionsCount((prev) => Math.max(0, prev ? prev - 1 : 0));
      },

      removeUserFromConversation: (conversationId: string, userId: string) => {
        conversationUpdater(conversationId, (conversation) => ({
          ...conversation,
          memberIds: conversation.memberIds.filter((memberId) => memberId !== userId),
        }));
      },
      addUserToConversation: (conversationId: string, userId: string) => {
        conversationUpdater(conversationId, (conversation) => ({
          ...conversation,
          memberIds: uniq([...conversation.memberIds, userId]),
        }));
      },
      invalidateMessagesCache: (conversationId: string) => {
        const queryKey = ['team-chat', conversationId, 'messages'];
        queryClient.removeQueries(queryKey);
      },
      invalidateAllRepliesCache: () => {
        removeRepliesQuery();
      },
      markConversationAsVisible: (conversationId: string) => {
        if (!isHideConversationEnabled || (!conversations?.dm.length && !conversations?.groups.length)) return;
        const conversation = [...conversations.dm, ...conversations.groups].find((c) => c.channelId === conversationId);
        /* 
          Since message.new is a very frequent event that we are going to get the conversation and check if it's hidden
          We have to check it here because event data sometimes might not tell if the channel is hidden or not, and if 
          the channel is hidden then only we would to like to mark it as show.
          FIXME: I will try to optimize in future, as there are array iterations twice.
        */
        if (!conversation?.isHidden) return;
        setConversations((prev) => {
          if (!prev) return prev;
          if (conversation?.type === 'DM') {
            return {
              ...prev,
              dm: prev.dm.map((c) => ({ ...c, ...(c.channelId === conversationId && { isHidden: false }) })),
            };
          } else {
            return {
              ...prev,
              groups: prev.groups.map((c) => ({ ...c, ...(c.channelId === conversationId && { isHidden: false }) })),
            };
          }
        });
      },
      markConversationAsHidden: (conversationId: string, type?: TeamChatTypes.ConversationType) => {
        setConversations((prev) => {
          if (!prev) return prev;
          if (type === 'DM') {
            return {
              ...prev,
              dm: prev.dm.map((c) => ({ ...c, ...(c.channelId === conversationId && { isHidden: true }) })),
            };
          } else {
            return {
              ...prev,
              groups: prev.groups.map((c) => ({ ...c, ...(c.channelId === conversationId && { isHidden: true }) })),
            };
          }
        });
      },
      setNoShowHideConversationModal: () => {
        setHideConversationModal(true);
      },
      setNoShowLinksSecurityModal: () => {
        setHideLinksSecurityModal(true);
      },
    };
  }, [
    setUsers,
    setConversations,
    setUnreadMentionsCount,
    setMentions,
    unreadMentionsCount,
    setQueryThreads,
    threadsParentIdMap,
    threadsUnreadCount,
    threadsQueryProps.refetch,
    refetchUnreadCount,
    conversations,
    setHideConversationModal,
    isHideConversationEnabled,
    setHideLinksSecurityModal,
  ]);

  const getUser = useCallback((userId: string) => users?.find((u) => u?.userID === userId), [users]);
  const getConversation = useCallback(
    (conversationId: string) =>
      conversations?.dm.find((c) => c.channelId === conversationId) ??
      conversations?.groups.find((c) => c.channelId === conversationId),
    [conversations]
  );

  const getConversationByMemberIds = useCallback(
    (memberIds: string[], type?: 'dm' | 'group', includeSelf = true) => {
      const searchMemberIds = includeSelf ? [...memberIds, clientUserId] : memberIds;
      const findInDms = () =>
        conversations?.dm.find(
          (c) =>
            c.memberIds.length === memberIds.length && //note in the DMs we don't include self
            c.memberIds.every((memberId) => memberIds.includes(memberId))
        );
      const findInGroups = () =>
        conversations?.groups.find(
          (c) =>
            c.memberIds.length === searchMemberIds.length && //in the channels we do include self in the search
            c.memberIds.every((memberId) => searchMemberIds.includes(memberId))
        );
      if (type === 'dm') return findInDms();
      if (type === 'group') return findInGroups();
      return findInDms() ?? findInGroups();
    },
    [conversations, clientUserId]
  );

  const helpers = useMemo(
    () => ({
      getUser,
      getConversation,
      getConversationByMemberIds,
    }),
    [getUser, getConversation, getConversationByMemberIds]
  );

  const unreadMessagesCount = useMemo(() => {
    const allConversations = [...(conversations?.groups ?? []), ...(conversations?.dm ?? [])];
    return allConversations.reduce((acc, conversation) => {
      if (!isHideConversationEnabled || !conversation.isHidden) {
        return acc + conversation.unreadCount;
      }
      return acc;
    }, 0);
  }, [conversations?.groups, conversations?.dm, isHideConversationEnabled]);

  const openTeamChat = useCallback(async () => {
    if (isAsleep) {
      await _wake();
    }

    if (!isOpen) {
      //Refetching conversations here because we need to re-watch them, and that happens in the fetch
      cache.invalidateAllRepliesCache();
      refetchConversations();
      refetchMentions();
      if (isThreadsSectionEnabled) {
        threadsQueryProps.refetch();
        refetchUnreadCount();
      }
    }
    setIsOpen(true);
    setHasBeenOpened(true);
    if (_setKeepTeamChatClientOpen) _setKeepTeamChatClientOpen(true);
    return;
  }, [isAsleep, isOpen, _wake, refetchConversations, refetchMentions, refetchUnreadCount, isThreadsSectionEnabled]);

  const closeTeamChat = useCallback(() => {
    _sleep();
    setIsOpen(false);
  }, [_sleep]);
  const focus = useCallback(() => setIsFocused(true), []);
  const unfocus = useCallback(() => setIsFocused(false), []);
  const openUserStatus = useCallback(() => _setIsCurrentUserStatusOpen(true), []);
  const closeUserStatus = useCallback(() => _setIsCurrentUserStatusOpen(false), []);
  const collapseNav = useCallback(() => setIsNavExpanded(false), []);
  const expandNav = useCallback(() => {
    //TODO: shouldn't be able expand on mobile
    setIsNavExpanded(true);
  }, []);

  /**
   * @param type:
   * static means the conversation members are set, and doesn't allow UI to adding/removing members (which changes the conversation )
   * dynamic means the conversation members are allowed to be modified (which changes the conversation id)
   */
  const openThread = useCallback((selectedThreadParentId: string, conversationId: string) => {
    _setSelectedThreadParentId(selectedThreadParentId);
    _setActiveConversationId(conversationId);
    _setMainPanelView('thread');
  }, []);
  const closeThread = useCallback((conversationId: string) => {
    _setSelectedThreadParentId(undefined);
    _setActiveConversationId(conversationId);
    _setMainPanelView('conversation-static');
  }, []);
  const openStaticConversation = useCallback(
    (conversationId: NonNullable<typeof activeConversationId>, highlightedMessageId?: string) => {
      // isAsleep will be true if the user clicks "view" on a notification
      openTeamChat().then(() => {
        _setActiveConversationId(conversationId);
        _setMainPanelView(`conversation-static`);
        _setHighlightedMessageId(highlightedMessageId);
        _setSelectedThreadParentId(undefined);
      });
    },
    [openTeamChat, _wake]
  );
  const openDynamicConversation = useCallback((conversationId: typeof activeConversationId) => {
    _setActiveConversationId(conversationId);
    _setMainPanelView(`conversation-dynamic`);
    _setHighlightedMessageId(undefined);
  }, []);
  const openMentions = useCallback(() => {
    _setMainPanelView('mentions');
    _setActiveConversationId(undefined);
    _setHighlightedMessageId(undefined);
    clearIsNavigatingFromThreads();
  }, []);
  const openThreadsSection = useCallback(() => {
    _setMainPanelView('threads-section');
    _setActiveConversationId(undefined);
    _setHighlightedMessageId(undefined);
    clearIsNavigatingFromThreads();
  }, []);
  const closeConversation = useCallback(() => {
    collapseNav();
    _setMainPanelView('none');
    _setActiveConversationId(undefined);
    _setSelectedThreadParentId(undefined);
    _setHighlightedMessageId(undefined);
  }, []);

  const restart = useCallback(async () => {
    collapseNav();
    _setActiveConversationId(undefined);
    _setSelectedThreadParentId(undefined);
    _setHighlightedMessageId(undefined);
    _setMainPanelView('none');
    _removeUsers();
    _removeMentions();
    setUnreadMentionsCount(0);
    _removeConversations();
    return disconnect().then(() => {
      setTimeout(() => {
        reInitialize();
      }, 1000);
    });
  }, [_removeConversations, _removeMentions, _removeUsers, collapseNav, disconnect, reInitialize]);

  const setIsNavigatingFromThreads = (value: boolean, parentMessage?: TeamChatTypes.Message) => {
    isNavigatingFromThreads.current = value;
    if (parentMessage) {
      parentMessageRef.current = parentMessage;
    }
  };
  const clearIsNavigatingFromThreads = () => {
    if (isNavigatingFromThreads.current) {
      isNavigatingFromThreads.current = false;
    }
    if (parentMessageRef.current) {
      parentMessageRef.current = undefined;
    }
  };

  type Conversation = NonNullable<typeof conversations>['dm' | 'groups'][number];
  type User = NonNullable<typeof users>[number];
  type Message = NonNullable<typeof messagesWithMentions>[number];

  const isDataLoading = isInitializing || isLoadingUsers || isLoadingConversations;
  const isDataAllFetched = isInitialized && isUsersFetched && isConversationsFetched;
  const dataError = initializationError || (usersError && !shouldFetchConversationUsers) || conversationsError;
  const isDataReady = isDataAllFetched && !dataError && !!user;
  const isTeamChatReady = isDataReady && !dataError && !!user && !!users?.length && isInitialized;

  /** When initializing team chat, we need to fetch all of the data, then put it to sleep (disconnect the user) while it's not in use */
  useEffect(() => {
    if (!isDataReady || isOpen) {
      return;
    }
    const timeout = setTimeout(() => {
      _sleep();
    }, 1000);
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isDataReady, isOpen]);

  //fully restart team chat after orgId changes
  const prevOrgId = useRef(orgId);
  useEffect(() => {
    if (orgId && orgId !== prevOrgId.current) {
      restart();
    }
    prevOrgId.current = orgId;
  }, [orgId]);

  useEffect(() => {
    return () => {
      destroy();
    };
  }, []);

  if (!otherUsers?.length && !shouldFetchConversationUsers && isTeamChatReady) {
    refetchUsers();
  }

  const data = {
    isMounted: true,
    api,
    user,
    orgId,
    mainPanelView,
    isOpen,
    hasBeenOpened,
    isCurrentUserStatusOpen,
    isFocused,
    isNavExpanded,
    isAsleep,
    selectedThreadParentId,
    isThread: !!selectedThreadParentId,

    //the storage hook isn't smart enough to know it's non nullable even though there is a default value
    notificationSettings: notificationSettings as NonNullable<typeof notificationSettings>,
    setNotificationSettings,

    openTeamChat,
    closeTeamChat,
    openUserStatus,
    closeUserStatus,
    expandNav,
    collapseNav,
    focus,
    unfocus,

    closeConversation,
    openStaticConversation,
    openDynamicConversation,
    openMentions,
    openThread,
    closeThread,
    openThreadsSection,

    isInitializing,
    isInitialized,
    initializationError,

    dataError,
    isDataLoading,
    isReady: isTeamChatReady,
    isDataReady,
    conversations,
    users,
    otherUsers,
    messagesWithMentions,
    isLoadingMentions,
    isMentionsFetched,
    mentionsError,
    unreadMentionsCount,
    unreadMessagesCount,
    activeConversationId,
    highlightedMessageId,

    createConversation,
    isCreatingConversation,
    createConversationError,
    updateConversation,
    isUpdatingConversation,
    updateConversationError,
    addMembersToConversation,
    isAddingMembers,
    removeMembersFromConversation,
    isRemovingMembers,
    deleteConversation,
    isDeletingConversation,
    deleteConversationError,
    isFetchingMentions,

    updateCurrentUser,
    isUpdatingUser,
    updateUserError,

    cache,
    helpers,

    refetchUsers,
    refetchConversations,
    refetchMentions,
    restart,
    setUnreadMentionsCount,
    setMentions,
    setReplies,

    isThreadsSectionEnabled,
    isSmallModeEnabled,
    isHideConversationEnabled,
    hideConversationModal,

    // threads query props
    threadsQueryProps,
    threadsUnreadCount,
    isNavigatingFromThreads: isNavigatingFromThreads.current,
    setIsNavigatingFromThreads,
    clearIsNavigatingFromThreads,
    threadsParentIdMap,
    parentMessageRef: parentMessageRef.current,

    // Team chat media Links Support
    hideLinksSecurityModal,

    // Users By Name
    shouldFetchConversationUsers,
    addUserPresence,

    //keyword search
    isKeywordSearchEnabled,
  };
  return data;
};

export const useTeamChatSelector = <T extends any>(selector: (ctx: ReturnType<typeof useTeamChatProvider>) => T) => {
  const data = useContextSelector(TeamChatContext, (ctx) => selector(ctx as ReturnType<typeof useTeamChatProvider>));
  return data as typeof data;
};

export const useTeamChat = <T extends (keyof TeamChatContextValue)[]>(values: T) => {
  const data = useContextSelector(TeamChatContext, (context) =>
    values.reduce((acc, key) => ({ ...acc, [key]: context?.[key] }), {} as Pick<TeamChatContextValue, T[number]>)
  );
  return data;
};

export const TeamChatProvider = ({ children, ...props }: ComponentProps<typeof _TeamChatProviderInner>) => {
  // Get user message details to determine if user has any unread messages
  const { hasUnreadMessages } = useUserMessageDetails(props.orgId, props.userId);
  const [keepTeamChatClientOpen, setKeepTeamChatClientOpen] = useState(false);

  // Expose the setKeepTeamChatClientOpen function to the inner provider
  const extendedProps = {
    ...props,
    _setKeepTeamChatClientOpen: setKeepTeamChatClientOpen,
  };

  // Determine if Stream client should be initialized
  const shouldEnableClient = keepTeamChatClientOpen || hasUnreadMessages;

  switch (props.api) {
    case 'stream':
      return (
        <_StreamAPIProvider {...props} enabled={shouldEnableClient}>
          <_TeamChatProviderInner {...extendedProps}>{children}</_TeamChatProviderInner>
        </_StreamAPIProvider>
      );
    case 'mock':
      return <_StreamAPIProvider {...props}>{children}</_StreamAPIProvider>;
    default:
      throw new Error(`Unsupported API type: ${props.api}`);
  }
};
TeamChatProvider.displayName = 'TeamChatProvider';
